import type { Finding } from '@complan/contracts';
import { ApiRequestError } from '@/shared/api/api-error';
import type { ResolvableFinding } from '@/shared/i18n';

/**
 * Turning a thrown admin write error into the findings the screen renders
 * (task 23.6).
 *
 * Every admin write that a rule can reject — a referenced delete (R16.6), a
 * Platform colour violation (R16.12), a duplicate active name (R16.11), a
 * create/delete against a fixed value set (R16.10), a change to a
 * non-configurable rule (R8.9) — comes back from the API as a `validation_failed`
 * response carrying `findings`. `apiFetch` throws those as an
 * {@link ApiRequestError}. This module is the one place that reaches into the
 * thrown error and hands the finding set to the component, so no screen re-derives
 * a rule verdict: the server decides, the client surfaces.
 *
 * A finding never carries user-facing text; {@link toResolvableFinding} maps it to
 * the {@link ResolvableFinding} shape the shared `resolveFindingMessage` resolves
 * against the i18next catalogue, carrying the finding's own `detail` as
 * interpolation variables and its administrator override (if any) as the winning
 * text.
 */

/** The findings a thrown admin error carries, or an empty list for any other error. */
export function findingsFromError(error: unknown): readonly Finding[] {
  if (error instanceof ApiRequestError && error.body.code === 'validation_failed') {
    return error.body.findings;
  }
  return [];
}

/** True when the thrown error is the unknown-record `not_found` (404). */
export function isNotFoundError(error: unknown): boolean {
  return error instanceof ApiRequestError && error.body.code === 'not_found';
}

/** The exact number of referencing Initiatives a refused delete reports (R16.6), or null. */
export function referencingCountFromError(error: unknown): number | null {
  const referenced = findingsFromError(error).find(
    (finding) => finding.ruleId === MASTER_DATA_REFERENCED_RULE_ID,
  );
  const count = referenced?.detail?.['referencingInitiativeCount'];
  return typeof count === 'number' ? count : null;
}

/** Maps a wire {@link Finding} to the {@link ResolvableFinding} the shared resolver takes. */
export function toResolvableFinding(finding: Finding): ResolvableFinding {
  return {
    messageKey: finding.messageKey,
    messageOverride: finding.message,
    detail: finding.detail ?? undefined,
  };
}

/**
 * The rule id the reference-protection finding carries when a delete is refused
 * because at least one Initiative references the record (R16.6). Mirrors the
 * server's `MASTER_DATA_REFERENCED_RULE_ID`; kept here so the client can single
 * out that finding to show the count and the deactivation offer.
 */
export const MASTER_DATA_REFERENCED_RULE_ID = 'HV-MD-REFERENCED';
